import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSubmitGroupReturnRequestMutation } from '../../../redux/apis/endpoints/requestApi';
import { useGetFulfillmentGroupDetailsQuery } from '../../../redux/apis/endpoints/fulfillmentGroupApi';
import Button from '../../ui/Button';
import Modal from '../../ui/Modal';
import { toast } from 'react-hot-toast';

const userLang = navigator.language || navigator.userLanguage;

const GroupReturnRequestButton = ({ fulfillmentGroupId, onSuccess }) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [comments, setComments] = useState('');

  const [submitGroupReturnRequest, { isLoading: isSubmitting }] = useSubmitGroupReturnRequestMutation();
  
  // Get fulfillment group details to determine eligible items
  const { data: fulfillmentGroup } = useGetFulfillmentGroupDetailsQuery(fulfillmentGroupId, {
    skip: !fulfillmentGroupId
  });

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reason) {
      toast.error(userLang === 'zh-CN' ? '请选择退货原因' : 'Please select a return reason');
      return;
    }

    // Get all eligible items for return from this fulfillment group
    const eligibleItems = fulfillmentGroup?.orderItems?.filter(item => 
      ['delivered', 'pickup_completed_by_customer'].includes(item.status) && 
      item.type === 'buy'
    ) || [];

    if (eligibleItems.length === 0) {
      toast.error(userLang === 'zh-CN' ? '没有符合退货条件的商品' : 'No items are eligible for return');
      return;
    }

    try {
      const result = await submitGroupReturnRequest({
        fulfillmentGroupId,
        orderItemIds: eligibleItems.map(item => item.id), // Pass all eligible item IDs
        reason,
        comments
      }).unwrap();

      if (result.success) {
        toast.success(userLang === 'zh-CN' ? '退货申请已提交' : 'Return request submitted successfully');
        handleCloseModal();
        if (onSuccess) onSuccess();
      } else {
        toast.error(result.message || (userLang === 'zh-CN' ? '申请提交失败' : 'Failed to submit request'));
      }
    } catch (error) {
      console.error('Error submitting group return request:', error);
      toast.error(error?.data?.message || (userLang === 'zh-CN' ? '申请提交失败' : 'Failed to submit request'));
    }
  };

  const reasonOptions = [
    { value: 'changed_mind', label: userLang === 'zh-CN' ? '改变主意' : 'Changed mind' },
    { value: 'wrong_size', label: userLang === 'zh-CN' ? '尺寸不合适' : 'Wrong size' },
    { value: 'defective', label: userLang === 'zh-CN' ? '产品有缺陷' : 'Defective product' },
    { value: 'not_as_described', label: userLang === 'zh-CN' ? '与描述不符' : 'Not as described' },
    { value: 'other', label: userLang === 'zh-CN' ? '其他原因' : 'Other reason' },
  ];

  return (
    <>
      <Button 
        onClick={handleOpenModal} 
        variant="primary"
        className="w-full"
      >
        {userLang === 'zh-CN' ? '申请整组退货' : 'Request Return'}
      </Button>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={userLang === 'zh-CN' ? '申请整组退货' : 'Request Return'}
      >
        <div className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <p className="text-gray-700 mb-4">
                {userLang === 'zh-CN' 
                  ? '此操作将为该订单组中的所有商品申请退货。' 
                  : 'This action will request returns for all eligible items in this order group.'}
              </p>
              <label htmlFor="return-reason" className="block text-sm font-medium text-gray-700 mb-1">
                {userLang === 'zh-CN' ? '退货原因' : 'Return Reason'} <span className="text-red-600">*</span>
              </label>
              <select
                id="return-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                required
              >
                <option value="">{userLang === 'zh-CN' ? '请选择原因' : 'Select a reason'}</option>
                {reasonOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="mb-6">
              <label htmlFor="return-comments" className="block text-sm font-medium text-gray-700 mb-1">
                {userLang === 'zh-CN' ? '额外备注' : 'Additional Comments'}
              </label>
              <textarea
                id="return-comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={4}
                className="mt-1 block w-full shadow-sm sm:text-sm focus:ring-blue-500 focus:border-blue-500 border border-gray-300 rounded-md"
                placeholder={userLang === 'zh-CN' ? '请提供额外信息（选填）' : 'Provide any additional information (optional)'}
              />
            </div>
            <div className="flex justify-end space-x-4">
              <Button variant="secondary" onClick={handleCloseModal} disabled={isSubmitting}>
                {userLang === 'zh-CN' ? '取消' : 'Cancel'}
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting} isLoading={isSubmitting}>
                {userLang === 'zh-CN' ? '提交申请' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default GroupReturnRequestButton; 